import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import {
  Box,
  Typography,
  Button,
  Alert,
  Modal,
  CircularProgress,
  Chip,
  useTheme,
} from "@mui/material";
import EditRole from "./EditRole";

export default function ListRoles({ refresh, onRefresh }) {
  const theme = useTheme();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);

  useEffect(() => {
    fetchRoles();
  }, [refresh]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/roles");
      if (response.data.success) {
        const data =
          typeof response.data.data === "string"
            ? JSON.parse(response.data.data)
            : response.data.data;
        const roleOrder = { admin: 0, employee: 1, customer: 2, student: 3 };
        const sorted = (data.roles || []).sort((a, b) => {
          const aOrder = roleOrder[a.name?.toLowerCase()] ?? 99;
          const bOrder = roleOrder[b.name?.toLowerCase()] ?? 99;
          return aOrder - bOrder;
        });
        setRoles(sorted);
        setError(null);
      } else {
        setError(response.data.message || "Failed to fetch roles");
      }
    } catch (err) {
      setError("Error fetching roles: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (roleId, isSystemRole) => {
    if (isSystemRole) {
      setError("Cannot delete global system roles (Admin, Employee, Customer, Student)");
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (!window.confirm("Are you sure you want to delete this role?")) return;

    try {
      const response = await axiosInstance.delete(`/api/roles/${roleId}`);
      if (response.data.success) {
        setRoles(roles.filter((r) => r.id !== roleId));
        setSuccess("Role deleted successfully");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.data.message || "Failed to delete role");
      }
    } catch (err) {
      setError("Error deleting role: " + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = (role) => {
    setCurrentRole(role);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentRole(null);
  };

  const updateModal = () => {
    fetchRoles();
    closeModal();
    if (onRefresh) onRefresh();
  };

  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
        }`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
        }`,
      },
    },
    expanderRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
      },
    },
  };

  const columns = [
    {
      name: "Role Name",
      selector: (row) => row.name,
      sortable: true,
      minWidth: '200px',
      cell: (row) => {
        const roleColors = {
          admin: { bg: '#f3e8ff', color: '#7c3aed' },
          employee: { bg: '#fef3c7', color: '#d97706' },
          customer: { bg: '#d1fae5', color: '#059669' },
          student: { bg: '#f3e8ff', color: '#8b5cf6' },
        };
        const roleKey = row.name?.toLowerCase();
        const roleStyle = roleColors[roleKey];
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.name.charAt(0).toUpperCase() + row.name.slice(1)}
            </Typography>
            {roleStyle && row.is_system_role ? (
              <Chip
                label="Global"
                size="small"
                sx={{ bgcolor: roleStyle.bg, color: roleStyle.color, fontWeight: 600 }}
              />
            ) : (
              <Chip
                label={row.is_system_role ? "Global" : "Custom"}
                color={row.is_system_role ? "success" : "info"}
                size="small"
                variant={row.is_system_role ? "filled" : "outlined"}
              />
            )}
          </Box>
        );
      },
    },
    {
      name: "Description",
      selector: (row) => row.description || "No description",
      wrap: true,
      minWidth: '250px',
    },
    {
      name: "Panel Access",
      cell: (row) => {
        const roleKey = row.name?.toLowerCase();
        const allPanels = ['admin', 'employee'].includes(roleKey);
        const isStudent = roleKey === 'student';
        const panelLabel = allPanels ? 'All Panels' : isStudent ? 'Student Portal' : 'Customer Panel';
        const panelColor = allPanels ? '#d97706' : isStudent ? '#8b5cf6' : '#10b981';
        return (
          <Chip
            label={panelLabel}
            variant="outlined"
            size="small"
            sx={{
              borderColor: panelColor,
              color: panelColor,
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      name: "Permissions",
      selector: (row) => row.permissions_count,
      cell: (row) => (
        <Chip
          label={`${row.permissions_count} submodule${row.permissions_count !== 1 ? "s" : ""}`}
          color="secondary"
          size="small"
        />
      ),
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <Chip
          label={row.is_active ? "Active" : "Inactive"}
          color={row.is_active ? "success" : "warning"}
          size="small"
        />
      ),
      sortable: true,
    },
    {
      name: "Created",
      selector: (row) =>
        row.created_at ? new Date(row.created_at).toLocaleDateString() : "N/A",
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleEdit(row)}
          >
            <i className={`fa ${row.is_system_role ? "fa-eye" : "fa-pencil"}`} />
          </Button>
          {!row.is_system_role && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => handleDelete(row.id, row.is_system_role)}
            >
              <i className="fa fa-trash-o" />
            </Button>
          )}
        </Box>
      ),
      ignoreRowClick: true,
    },
  ];

  const muiModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: "80%", md: "70%" },
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    outline: "none",
    p: 0,
  };

  const ExpandedComponent = ({ data }) => (
    <Box p={2}>
      <Typography variant="body2" color="text.primary" mb={1}>
        <strong>Submodule Permissions:</strong>
      </Typography>

      {data.permissions && data.permissions.length > 0 ? (
        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.875rem",
            "& th": {
              padding: "8px",
              textAlign: "left",
              borderBottom: `1px solid ${theme.palette.divider}`,
              fontWeight: 600,
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
            },
            "& td": {
              padding: "8px",
              borderBottom: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
            },
            "& .fa-check": {
              color: theme.palette.success.main,
            },
            "& .fa-times": {
              color: theme.palette.error.main,
            },
          }}
        >
          <thead>
            <tr>
              <th>Submodule</th>
              <th align="center">View</th>
              <th align="center">Add</th>
              <th align="center">Edit</th>
              <th align="center">Delete</th>
            </tr>
          </thead>
          <tbody>
            {data.permissions.map((perm, i) => (
              <tr key={i}>
                <td>{perm.module_name}</td>
                <td align="center">
                  <i className="fa fa-check" />
                </td>
                <td align="center">
                  {perm.can_add ? (
                    <i className="fa fa-check" />
                  ) : (
                    <i className="fa fa-times" />
                  )}
                </td>
                <td align="center">
                  {perm.can_edit ? (
                    <i className="fa fa-check" />
                  ) : (
                    <i className="fa fa-times" />
                  )}
                </td>
                <td align="center">
                  {perm.can_delete ? (
                    <i className="fa fa-check" />
                  ) : (
                    <i className="fa fa-times" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No permissions assigned to this role.
        </Typography>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        <i className="fa fa-info-circle" /> Only submodules have configurable permissions.
        {data.is_system_role && (
          <>
            <br />
            <strong>Global Role:</strong> This role is shared across all teams and cannot be modified.
          </>
        )}
      </Alert>
    </Box>
  );

  const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}
      >
        <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
          Roles & Permissions
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={fetchRoles}
          disabled={loading}
          sx={{
            fontFamily,
            textTransform: 'none',
            borderRadius: '10px',
            px: 2,
            py: 0.75,
            borderColor: 'rgba(0,0,0,0.15)',
            color: '#475569',
            '&:hover': { borderColor: '#ec4899', color: '#ec4899' },
          }}
        >
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <i className="fa fa-info-circle" /> <strong>System Roles:</strong>{" "}
        <Chip label="Admin" size="small" sx={{ bgcolor: '#f3e8ff', color: '#7c3aed', fontWeight: 600, mx: 0.25 }} /> &{" "}
        <Chip label="Employee" size="small" sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 600, mx: 0.25 }} /> (All Panels) |{" "}
        <Chip label="Customer" size="small" sx={{ bgcolor: '#d1fae5', color: '#059669', fontWeight: 600, mx: 0.25 }} /> (Customer Panel) |{" "}
        <Chip label="Student" size="small" sx={{ bgcolor: '#f3e8ff', color: '#8b5cf6', fontWeight: 600, mx: 0.25 }} /> (Student Portal) |{" "}
        <Chip label="Custom" color="info" size="small" sx={{ mx: 0.25 }} /> team-specific
      </Alert>

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
          <CircularProgress size={30} />
          <Typography mt={2}>Loading roles...</Typography>
        </Box>
      ) : roles.length === 0 ? (
        <Box textAlign="center" py={4}>
          <i className="fa fa-shield fa-3x text-muted" />
          <Typography mt={2} color="text.primary">
            No roles found
          </Typography>
        </Box>
      ) : (
        <DataTable
          columns={columns}
          data={roles}
          pagination
          responsive
          highlightOnHover
          pointerOnHover
          expandableRows
          expandableRowsComponent={ExpandedComponent}
          customStyles={customDataTableStyles}
          theme={theme.palette.mode === "dark" ? "dark" : "default"}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <i className="fa fa-exclamation-triangle" /> {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <i className="fa fa-check" /> {success}
        </Alert>
      )}

      <Modal open={isModalOpen} onClose={closeModal} aria-labelledby="edit-role-modal">
        <Box sx={muiModalStyle}>
          <EditRole role={currentRole} onClose={closeModal} onUpdate={updateModal} />
        </Box>
      </Modal>
    </Box>
  );
}